import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { IconComponent } from '@examdojo/core/icon';
import { MarkdownViewerComponent } from '@examdojo/core/ui/markdown-viewer';
import { ModalController } from '@ionic/angular/standalone';

import type { TestResult } from '../test-runner/test-runner.model';
import { CriticalValuesTableComponent } from './critical-values-table.component';
import { NormalDistributionDiagramComponent } from './normal-distribution-diagram.component';

// ---------------------------------------------------------------------------
// Dialog data
// ---------------------------------------------------------------------------

export interface CriticalTableDialogData {
  readonly result: TestResult;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

@Component({
  selector: 'dojo-critical-table-dialog',
  standalone: true,
  imports: [IconComponent, MarkdownViewerComponent, CriticalValuesTableComponent, NormalDistributionDiagramComponent],
  templateUrl: './critical-table-dialog.component.html',
  styleUrl: './critical-table-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CriticalTableDialogComponent {
  constructor(private readonly modalCtrl: ModalController) {}

  readonly result = input.required<TestResult>();

  // ── Diagram alpha (hover or default) ────────────────────────────────────

  private readonly hoveredAlpha = signal<number | null>(null);
  readonly diagramAlpha = computed(() => this.hoveredAlpha() ?? this.result().alpha);

  // ── Lookup guide ────────────────────────────────────────────────────────

  readonly dfLabel = computed(() => {
    const df = this.result().degreesOfFreedom;
    return Number.isInteger(df) ? String(df) : df.toFixed(1);
  });

  readonly alphaLabel = computed(() => String(this.result().alpha));

  readonly criticalValueLabel = computed(() => {
    const r = this.result();
    const cv = r.criticalValues.length === 2 ? r.criticalValues[1] : r.criticalValues[0];
    return r.distributionType === 't' ? `$t^* = ${cv.toFixed(4)}$` : `$\\chi^2_{\\text{crit}} = ${cv.toFixed(4)}$`;
  });

  readonly conclusionLine = computed(() => {
    const r = this.result();

    if (r.distributionType === 't') {
      const cv = r.criticalValues.length === 2 ? r.criticalValues[1] : r.criticalValues[0];

      if (r.tailType === 'two') {
        const absT = Math.abs(r.testStatistic);
        const op = r.rejectNull ? '>' : '<';
        const verdict = r.rejectNull ? '\\text{Reject } H_0' : '\\text{Fail to reject } H_0';
        return `$|t| = ${absT.toFixed(4)} ${op} ${cv.toFixed(4)} = t^* \\implies ${verdict}$`;
      }

      const critVal = r.tailType === 'left' ? -cv : cv;
      const op = r.rejectNull ? (r.tailType === 'right' ? '>' : '<') : r.tailType === 'right' ? '<' : '>';
      const verdict = r.rejectNull ? '\\text{Reject } H_0' : '\\text{Fail to reject } H_0';
      return `$t = ${r.testStatistic.toFixed(4)} ${op} ${critVal.toFixed(4)} = t^* \\implies ${verdict}$`;
    }

    if (r.distributionType === 'chi-squared') {
      const cv = r.criticalValues[0];
      const op = r.rejectNull ? '>' : '<';
      const verdict = r.rejectNull ? '\\text{Reject } H_0' : '\\text{Fail to reject } H_0';
      return `$\\chi^2 = ${r.testStatistic.toFixed(4)} ${op} ${cv.toFixed(
        4,
      )} = \\chi^2_{\\text{crit}} \\implies ${verdict}$`;
    }

    return '';
  });

  // ── Actions ─────────────────────────────────────────────────────────────

  onAlphaHover(alpha: number | null): void {
    this.hoveredAlpha.set(alpha);
  }

  close(): void {
    this.modalCtrl.dismiss();
  }
}
